import { useState } from "react";
import { apiCall } from "../api";
import { CONTACT_EMAIL } from "../legal";
import { SimplePage, inputClass, buttonClass } from "./SimplePage";

const formatDate = (value) => (value ? new Date(value).toLocaleDateString('es-MX') : '');

// Dispute page for evaluated people: verify the email with a code, then dispute a rating
const TenantPortal = () => {
  const [step, setStep] = useState('email'); // 'email' | 'code' | 'ratings'
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [token, setToken] = useState(null);
  const [ratings, setRatings] = useState([]);
  const [disputing, setDisputing] = useState(null);
  const [dispute, setDispute] = useState({ tenant_name: '', dispute_reason: '', dispute_details: '' });
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const run = async (action) => {
    setLoading(true);
    setError('');
    try {
      await action();
    } catch (err) {
      setError(err.detail || 'Algo salió mal. Intenta de nuevo.');
    } finally {
      setLoading(false);
    }
  };

  const requestCode = (e) => {
    e.preventDefault();
    run(async () => {
      const result = await apiCall('/public/tenant-portal/request-code', 'POST', { email });
      setMessage(result.message);
      setStep('code');
    });
  };

  const loadRatings = async (authToken) => {
    setRatings(await apiCall('/public/tenant-portal/ratings', 'GET', null, authToken));
  };

  const verifyCode = (e) => {
    e.preventDefault();
    run(async () => {
      const result = await apiCall('/public/tenant-portal/verify', 'POST', { email, code });
      setToken(result.token);
      await loadRatings(result.token);
      setMessage('');
      setStep('ratings');
    });
  };

  const submitDispute = (e) => {
    e.preventDefault();
    run(async () => {
      const result = await apiCall('/public/tenant-portal/disputes', 'POST', { rating_id: disputing, ...dispute }, token);
      setMessage(result.message);
      setDisputing(null);
      setDispute({ tenant_name: '', dispute_reason: '', dispute_details: '' });
      await loadRatings(token);
    });
  };

  return (
    <SimplePage title="Disputar una evaluación">
      {step === 'email' && (
        <form onSubmit={requestCode} className="space-y-4">
          <p className="text-gray-700">Si un propietario te evaluó y no estás de acuerdo, escribe el correo con el que te registró. Te enviaremos un código para ver tus evaluaciones y disputarlas.</p>
          <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className={inputClass} placeholder="tu@correo.com" />
          <button type="submit" disabled={loading} className={buttonClass}>{loading ? 'Enviando...' : 'Enviarme un código'}</button>
        </form>
      )}

      {step === 'code' && (
        <form onSubmit={verifyCode} className="space-y-4">
          <p className="text-gray-700">{message}</p>
          <input inputMode="numeric" required value={code} onChange={(e) => setCode(e.target.value)} className={inputClass} placeholder="Código de 6 dígitos" />
          <button type="submit" disabled={loading} className={buttonClass}>{loading ? 'Verificando...' : 'Ver mis evaluaciones'}</button>
          <button type="button" onClick={() => setStep('email')} className="text-sm text-amber-700 underline">Usar otro correo</button>
        </form>
      )}

      {step === 'ratings' && (
        <div className="space-y-4">
          {message && <p className="bg-emerald-50 border border-emerald-200 text-emerald-800 rounded-lg p-3 text-sm">{message}</p>}
          {ratings.length === 0 && <p className="text-gray-700">No hay evaluaciones registradas con este correo.</p>}
          {ratings.map((rating) => (
            <div key={rating.id} className="border border-gray-200 rounded-lg p-4">
              <div className="flex flex-wrap justify-between gap-2 mb-2">
                <span className="font-semibold text-gray-900">Calificación general: {rating.overall_rating}/5</span>
                <span className="text-sm text-gray-500">{formatDate(rating.created_at)}</span>
              </div>
              {rating.property_address && <p className="text-sm text-gray-600">Inmueble: {rating.property_address}</p>}
              <p className="text-sm text-gray-600">Periodo: {rating.rental_period_start} a {rating.rental_period_end}</p>
              {rating.comments && <p className="text-sm text-gray-700 mt-2">"{rating.comments}"</p>}

              {rating.disputed ? (
                <p className="mt-3 inline-block bg-gray-100 border border-gray-300 text-gray-700 text-xs font-semibold px-2 py-0.5 rounded-full">En disputa</p>
              ) : disputing === rating.id ? (
                <form onSubmit={submitDispute} className="mt-4 space-y-3">
                  <input required value={dispute.tenant_name} onChange={(e) => setDispute(prev => ({ ...prev, tenant_name: e.target.value }))} className={inputClass} placeholder="Tu nombre completo" />
                  <select required value={dispute.dispute_reason} onChange={(e) => setDispute(prev => ({ ...prev, dispute_reason: e.target.value }))} className={inputClass}>
                    <option value="">Motivo</option>
                    <option value="Información falsa o incorrecta">Información falsa o incorrecta</option>
                    <option value="No fui inquilino de este inmueble">No fui inquilino de este inmueble</option>
                    <option value="El adeudo ya fue pagado">El adeudo ya fue pagado</option>
                    <option value="Otro">Otro</option>
                  </select>
                  <textarea required rows={4} value={dispute.dispute_details} onChange={(e) => setDispute(prev => ({ ...prev, dispute_details: e.target.value }))} className={inputClass} placeholder="Explica qué es incorrecto" />
                  <div className="flex gap-3">
                    <button type="submit" disabled={loading} className={buttonClass}>{loading ? 'Enviando...' : 'Enviar disputa'}</button>
                    <button type="button" onClick={() => setDisputing(null)} className="px-4 text-sm text-gray-600 underline">Cancelar</button>
                  </div>
                </form>
              ) : (
                <button onClick={() => { setDisputing(rating.id); setMessage(''); }} className="mt-3 text-sm font-semibold text-amber-700 underline">Disputar esta evaluación</button>
              )}
            </div>
          ))}
        </div>
      )}

      {error && <p className="text-red-600 text-sm mt-4">{error}</p>}

      <p className="text-xs text-gray-500 mt-8">¿No te registraron con correo o necesitas corregir o eliminar tus datos? Escríbenos a <a className="underline" href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>.</p>
    </SimplePage>
  );
};

export { TenantPortal };
